import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Package, ShoppingCart, CreditCard, Tag, Star, Settings,
  Plus, Search, Filter, MoreVertical, Edit2, Trash2, Eye, EyeOff,
  TrendingUp, DollarSign, Users, Clock, CheckCircle, XCircle,
  ChevronDown, ChevronRight, Upload, Image, Save, Layers, BarChart3,
  Percent, Calendar, Mail, RefreshCw, Globe2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import CommerceExtensionsPanel from '../components/ecommerce/CommerceExtensionsPanel';

const EcommercePanel = () => {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState({});
  
  // Modal states
  const [showProductModal, setShowProductModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Form states
  const [productForm, setProductForm] = useState({
    name: '', description: '', price: '', compare_at_price: '', sku: '',
    stock: '', category_id: '', is_active: true, is_featured: false,
    images: []
  });
  const [discountForm, setDiscountForm] = useState({
    code: '', name: '', type: 'percentage', value: '', min_purchase: '',
    max_uses: '', is_active: true
  });
  const [planForm, setPlanForm] = useState({
    name: '', description: '', price: '', interval: 'month',
    features: '', is_active: true, is_popular: false
  });
  
  const token = localStorage.getItem('token');
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: BarChart3 },
    { id: 'products', name: 'Products', icon: Package },
    { id: 'orders', name: 'Orders', icon: ShoppingCart },
    { id: 'subscriptions', name: 'Subscriptions', icon: CreditCard },
    { id: 'discounts', name: 'Discounts', icon: Tag },
    { id: 'reviews', name: 'Reviews', icon: Star },
    { id: 'commerce', name: 'Commerce+', icon: Globe2 },
    { id: 'settings', name: 'Settings', icon: Settings }
  ];

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [statsRes, productsRes, categoriesRes, ordersRes, plansRes, subscriptionsRes, discountsRes, reviewsRes, settingsRes] = await Promise.all([
        fetch(`${API_URL}/api/websites/${websiteId}/ecommerce-stats`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/products`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/categories`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/orders`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/subscription-plans`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/subscriptions`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/discounts`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/reviews`, { headers }),
        fetch(`${API_URL}/api/websites/${websiteId}/ecommerce-settings`, { headers })
      ]);
      
      if (statsRes.ok) setStats(await statsRes.json());
      if (productsRes.ok) setProducts(await productsRes.json());
      if (categoriesRes.ok) setCategories(await categoriesRes.json());
      if (ordersRes.ok) setOrders(await ordersRes.json());
      if (plansRes.ok) setSubscriptionPlans(await plansRes.json());
      if (subscriptionsRes.ok) setSubscriptions(await subscriptionsRes.json());
      if (discountsRes.ok) setDiscounts(await discountsRes.json());
      if (reviewsRes.ok) setReviews(await reviewsRes.json());
      if (settingsRes.ok) setSettings(await settingsRes.json());
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load e-commerce data');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteId]);

  // Product CRUD
  const saveProduct = async () => {
    try {
      const method = editingItem ? 'PUT' : 'POST';
      const url = editingItem 
        ? `${API_URL}/api/websites/${websiteId}/products/${editingItem.id}`
        : `${API_URL}/api/websites/${websiteId}/products`;
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ...productForm,
          price: parseFloat(productForm.price) || 0,
          compare_at_price: productForm.compare_at_price ? parseFloat(productForm.compare_at_price) : null,
          stock: parseInt(productForm.stock) || 0
        })
      });
      
      if (response.ok) {
        toast.success(editingItem ? 'Product updated!' : 'Product created!');
        setShowProductModal(false);
        setEditingItem(null);
        resetProductForm();
        fetchAllData();
      }
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const deleteProduct = async (productId) => {
    if (!confirm('Delete this product?')) return;
    try {
      await fetch(`${API_URL}/api/websites/${websiteId}/products/${productId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Product deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const resetProductForm = () => {
    setProductForm({
      name: '', description: '', price: '', compare_at_price: '', sku: '',
      stock: '', category_id: '', is_active: true, is_featured: false, images: []
    });
  };

  // Discount CRUD
  const saveDiscount = async () => {
    try {
      const method = editingItem ? 'PUT' : 'POST';
      const url = editingItem 
        ? `${API_URL}/api/websites/${websiteId}/discounts/${editingItem.id}`
        : `${API_URL}/api/websites/${websiteId}/discounts`;
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ...discountForm,
          value: parseFloat(discountForm.value) || 0,
          min_purchase: parseFloat(discountForm.min_purchase) || 0,
          max_uses: parseInt(discountForm.max_uses) || 0
        })
      });
      
      if (response.ok) {
        toast.success(editingItem ? 'Discount updated!' : 'Discount created!');
        setShowDiscountModal(false);
        setEditingItem(null);
        setDiscountForm({ code: '', name: '', type: 'percentage', value: '', min_purchase: '', max_uses: '', is_active: true });
        fetchAllData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to save discount');
      }
    } catch (error) {
      toast.error('Failed to save discount');
    }
  };

  const deleteDiscount = async (discountId) => {
    if (!confirm('Delete this discount?')) return;
    try {
      await fetch(`${API_URL}/api/websites/${websiteId}/discounts/${discountId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Discount deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete discount');
    }
  };

  // Subscription Plan CRUD
  const savePlan = async () => {
    try {
      const method = editingItem ? 'PUT' : 'POST';
      const url = editingItem 
        ? `${API_URL}/api/websites/${websiteId}/subscription-plans/${editingItem.id}`
        : `${API_URL}/api/websites/${websiteId}/subscription-plans`;
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          ...planForm,
          price: parseFloat(planForm.price) || 0,
          features: planForm.features.split('\n').filter(f => f.trim())
        })
      });
      
      if (response.ok) {
        toast.success(editingItem ? 'Plan updated!' : 'Plan created!');
        setShowPlanModal(false);
        setEditingItem(null);
        setPlanForm({ name: '', description: '', price: '', interval: 'month', features: '', is_active: true, is_popular: false });
        fetchAllData();
      }
    } catch (error) {
      toast.error('Failed to save plan');
    }
  };

  // Order status update
  const updateOrderStatus = async (orderId, status) => {
    try {
      await fetch(`${API_URL}/api/websites/${websiteId}/orders/${orderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status })
      });
      toast.success('Order status updated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  // Review moderation
  const moderateReview = async (reviewId, approved) => {
    try {
      await fetch(`${API_URL}/api/websites/${websiteId}/reviews/${reviewId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ is_approved: approved })
      });
      toast.success(approved ? 'Review approved' : 'Review rejected');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update review');
    }
  };

  // Settings update
  const saveSettings = async () => {
    try {
      await fetch(`${API_URL}/api/websites/${websiteId}/ecommerce-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(settings)
      });
      toast.success('Settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const formatCurrency = (amount) => {
    const code = settings.currency || 'USD';
    const locale = code === 'INR' ? 'en-IN' : 'en-US';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: code }).format(amount || 0);
    } catch {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      processing: 'bg-blue-500/20 text-blue-400',
      shipped: 'bg-purple-500/20 text-purple-400',
      delivered: 'bg-green-500/20 text-green-400',
      cancelled: 'bg-red-500/20 text-red-400',
      refunded: 'bg-zinc-500/20 text-zinc-400',
      active: 'bg-green-500/20 text-green-400',
      paused: 'bg-yellow-500/20 text-yellow-400',
      expired: 'bg-red-500/20 text-red-400'
    };
    return colors[status] || 'bg-zinc-500/20 text-zinc-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-violet-500/20 to-purple-500/20 rounded-xl p-6 border border-violet-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-sm">Total Revenue</p>
              <p className="text-3xl font-bold text-white mt-1">{formatCurrency(stats?.total_revenue || 0)}</p>
            </div>
            <DollarSign className="w-10 h-10 text-violet-400" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500/20 to-green-500/20 rounded-xl p-6 border border-emerald-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-sm">Total Orders</p>
              <p className="text-3xl font-bold text-white mt-1">{stats?.total_orders || 0}</p>
            </div>
            <ShoppingCart className="w-10 h-10 text-emerald-400" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl p-6 border border-blue-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-sm">Active Products</p>
              <p className="text-3xl font-bold text-white mt-1">{stats?.active_products || 0}</p>
            </div>
            <Package className="w-10 h-10 text-blue-400" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-xl p-6 border border-amber-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-sm">Pending Orders</p>
              <p className="text-3xl font-bold text-white mt-1">{stats?.pending_orders || 0}</p>
            </div>
            <Clock className="w-10 h-10 text-amber-400" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Recent Orders */}
        <div className="bg-zinc-900 rounded-xl border border-white/10 p-6">
          <h3 className="text-lg font-semibold mb-4">Recent Orders</h3>
          <div className="space-y-3">
            {(stats?.recent_orders || []).slice(0, 5).map(order => (
              <div key={order.id} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                <div>
                  <p className="font-medium">{order.order_number}</p>
                  <p className="text-sm text-zinc-400">{order.customer_email}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium">{formatCurrency(order.total)}</p>
                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(order.status)}`}>
                    {order.status}
                  </span>
                </div>
              </div>
            ))}
            {(!stats?.recent_orders || stats.recent_orders.length === 0) && (
              <p className="text-center text-zinc-500 py-8">No orders yet</p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-zinc-900 rounded-xl border border-white/10 p-6">
          <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => { resetProductForm(); setEditingItem(null); setShowProductModal(true); }}
              className="p-4 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center gap-3 transition-colors"
            >
              <Plus className="w-5 h-5 text-violet-400" />
              <span>Add Product</span>
            </button>
            <button
              onClick={() => { setEditingItem(null); setShowDiscountModal(true); }}
              className="p-4 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center gap-3 transition-colors"
            >
              <Tag className="w-5 h-5 text-emerald-400" />
              <span>Create Discount</span>
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              className="p-4 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center gap-3 transition-colors"
            >
              <ShoppingCart className="w-5 h-5 text-blue-400" />
              <span>View Orders</span>
            </button>
            <button
              onClick={() => { setEditingItem(null); setShowPlanModal(true); }}
              className="p-4 bg-zinc-800 hover:bg-zinc-700 rounded-lg flex items-center gap-3 transition-colors"
            >
              <CreditCard className="w-5 h-5 text-amber-400" />
              <span>Add Plan</span>
            </button>
          </div>
          
          {/* Alerts */}
          <div className="mt-6">
            <h4 className="text-sm font-medium text-zinc-400 mb-3">Alerts</h4>
            <div className="space-y-2">
              {stats?.pending_reviews > 0 && (
                <div className="flex items-center gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <Star className="w-5 h-5 text-amber-400" />
                  <span className="text-sm">{stats.pending_reviews} reviews pending approval</span>
                </div>
              )}
              {stats?.low_stock_products?.length > 0 && (
                <div className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <Package className="w-5 h-5 text-red-400" />
                  <span className="text-sm">{stats.low_stock_products.length} products low on stock</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderProducts = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              type="text"
              placeholder="Search products..."
              className="pl-10 pr-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white w-64"
            />
          </div>
          <select className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white">
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>
        <Button onClick={() => { resetProductForm(); setEditingItem(null); setShowProductModal(true); }} className="bg-violet-500">
          <Plus className="w-4 h-4 mr-2" />Add Product
        </Button>
      </div>

      <div className="bg-zinc-900 rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-800">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Product</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">SKU</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Price</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Stock</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Status</th>
              <th className="text-right px-6 py-4 text-sm font-medium text-zinc-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {products.map(product => (
              <tr key={product.id} className="hover:bg-zinc-800/50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-zinc-800 rounded-lg flex items-center justify-center">
                      {product.images?.[0]?.url ? (
                        <img src={product.images[0].url} alt="" className="w-full h-full object-cover rounded-lg" />
                      ) : (
                        <Package className="w-6 h-6 text-zinc-500" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{product.name}</p>
                      <p className="text-sm text-zinc-400">{product.short_description || 'No description'}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-zinc-400">{product.sku || '-'}</td>
                <td className="px-6 py-4">
                  <div>
                    <p className="font-medium">{formatCurrency(product.price)}</p>
                    {product.compare_at_price && (
                      <p className="text-sm text-zinc-500 line-through">{formatCurrency(product.compare_at_price)}</p>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={product.stock <= 5 ? 'text-red-400' : 'text-zinc-300'}>
                    {product.stock}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs ${product.is_active ? 'bg-green-500/20 text-green-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                    {product.is_active ? 'Active' : 'Draft'}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => {
                        setEditingItem(product);
                        setProductForm({
                          name: product.name,
                          description: product.description || '',
                          price: product.price?.toString() || '',
                          compare_at_price: product.compare_at_price?.toString() || '',
                          sku: product.sku || '',
                          stock: product.stock?.toString() || '',
                          category_id: product.category_id || '',
                          is_active: product.is_active,
                          is_featured: product.is_featured,
                          images: product.images || []
                        });
                        setShowProductModal(true);
                      }}
                      className="p-2 hover:bg-zinc-700 rounded-lg"
                    >
                      <Edit2 className="w-4 h-4 text-zinc-400" />
                    </button>
                    <button onClick={() => deleteProduct(product.id)} className="p-2 hover:bg-red-500/20 rounded-lg">
                      <Trash2 className="w-4 h-4 text-zinc-400 hover:text-red-400" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {products.length === 0 && (
          <div className="text-center py-12 text-zinc-500">
            <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No products yet</p>
            <Button onClick={() => { resetProductForm(); setShowProductModal(true); }} className="mt-4 bg-violet-500">
              Add Your First Product
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  const renderOrders = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <select className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white">
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <Button onClick={fetchAllData} variant="outline" className="border-zinc-700">
          <RefreshCw className="w-4 h-4 mr-2" />Refresh
        </Button>
      </div>

      <div className="bg-zinc-900 rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-800">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Order</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Customer</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Items</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Total</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Status</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Payment</th>
              <th className="text-right px-6 py-4 text-sm font-medium text-zinc-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {orders.map(order => (
              <tr key={order.id} className="hover:bg-zinc-800/50">
                <td className="px-6 py-4">
                  <p className="font-medium">{order.order_number}</p>
                  <p className="text-sm text-zinc-400">{new Date(order.created_at).toLocaleDateString()}</p>
                </td>
                <td className="px-6 py-4">
                  <p className="font-medium">{order.customer_name || 'Guest'}</p>
                  <p className="text-sm text-zinc-400">{order.customer_email}</p>
                </td>
                <td className="px-6 py-4 text-zinc-400">
                  {order.items?.length || 0} items
                </td>
                <td className="px-6 py-4 font-medium">
                  {formatCurrency(order.total)}
                </td>
                <td className="px-6 py-4">
                  <select
                    value={order.status}
                    onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                    className={`px-3 py-1 rounded-full text-xs ${getStatusColor(order.status)} bg-transparent border-0 cursor-pointer`}
                  >
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs ${order.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                    {order.payment_status}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <button className="p-2 hover:bg-zinc-700 rounded-lg">
                    <Eye className="w-4 h-4 text-zinc-400" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {orders.length === 0 && (
          <div className="text-center py-12 text-zinc-500">
            <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No orders yet</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderSubscriptions = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Subscription Plans</h3>
        <Button onClick={() => { setEditingItem(null); setShowPlanModal(true); }} className="bg-violet-500">
          <Plus className="w-4 h-4 mr-2" />Add Plan
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {subscriptionPlans.map(plan => (
          <div key={plan.id} className={`bg-zinc-900 rounded-xl border ${plan.is_popular ? 'border-violet-500' : 'border-white/10'} p-6 relative`}>
            {plan.is_popular && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-violet-500 text-white text-xs px-3 py-1 rounded-full">
                Popular
              </span>
            )}
            <h4 className="text-xl font-bold mb-2">{plan.name}</h4>
            <p className="text-zinc-400 text-sm mb-4">{plan.description}</p>
            <div className="mb-6">
              <span className="text-3xl font-bold">{formatCurrency(plan.price)}</span>
              <span className="text-zinc-400">/{plan.interval}</span>
            </div>
            <ul className="space-y-2 mb-6">
              {plan.features?.map((feature, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  {feature}
                </li>
              ))}
            </ul>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setEditingItem(plan);
                  setPlanForm({
                    name: plan.name,
                    description: plan.description || '',
                    price: plan.price?.toString() || '',
                    interval: plan.interval,
                    features: plan.features?.join('\n') || '',
                    is_active: plan.is_active,
                    is_popular: plan.is_popular
                  });
                  setShowPlanModal(true);
                }}
                variant="outline"
                size="sm"
                className="flex-1 border-zinc-700"
              >
                Edit
              </Button>
            </div>
          </div>
        ))}
        {subscriptionPlans.length === 0 && (
          <div className="col-span-3 text-center py-12 text-zinc-500 bg-zinc-900 rounded-xl border border-white/10">
            <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No subscription plans yet</p>
            <Button onClick={() => setShowPlanModal(true)} className="mt-4 bg-violet-500">
              Create Your First Plan
            </Button>
          </div>
        )}
      </div>

      {subscriptions.length > 0 && (
        <>
          <h3 className="text-lg font-semibold mt-8">Active Subscriptions</h3>
          <div className="bg-zinc-900 rounded-xl border border-white/10 overflow-hidden">
            <table className="w-full">
              <thead className="bg-zinc-800">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Customer</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Plan</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Status</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Started</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {subscriptions.map(sub => (
                  <tr key={sub.id}>
                    <td className="px-6 py-4">{sub.customer_email}</td>
                    <td className="px-6 py-4">{sub.plan_name}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(sub.status)}`}>
                        {sub.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-zinc-400">
                      {new Date(sub.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );

  const renderDiscounts = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Discount Codes</h3>
        <Button onClick={() => { setEditingItem(null); setDiscountForm({ code: '', name: '', type: 'percentage', value: '', min_purchase: '', max_uses: '', is_active: true }); setShowDiscountModal(true); }} className="bg-violet-500">
          <Plus className="w-4 h-4 mr-2" />Create Discount
        </Button>
      </div>

      <div className="bg-zinc-900 rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full">
          <thead className="bg-zinc-800">
            <tr>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Code</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Type</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Value</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Usage</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-zinc-400">Status</th>
              <th className="text-right px-6 py-4 text-sm font-medium text-zinc-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {discounts.map(discount => (
              <tr key={discount.id} className="hover:bg-zinc-800/50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-violet-400" />
                    <span className="font-mono font-bold">{discount.code}</span>
                  </div>
                  <p className="text-sm text-zinc-400 mt-1">{discount.name}</p>
                </td>
                <td className="px-6 py-4 text-zinc-400 capitalize">{discount.type.replace('_', ' ')}</td>
                <td className="px-6 py-4 font-medium">
                  {discount.type === 'percentage' ? `${discount.value}%` : formatCurrency(discount.value)}
                </td>
                <td className="px-6 py-4 text-zinc-400">
                  {discount.uses_count} / {discount.max_uses || '∞'}
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs ${discount.is_active ? 'bg-green-500/20 text-green-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                    {discount.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => {
                        setEditingItem(discount);
                        setDiscountForm({
                          code: discount.code,
                          name: discount.name || '',
                          type: discount.type,
                          value: discount.value?.toString() || '',
                          min_purchase: discount.min_purchase?.toString() || '',
                          max_uses: discount.max_uses?.toString() || '',
                          is_active: discount.is_active
                        });
                        setShowDiscountModal(true);
                      }}
                      className="p-2 hover:bg-zinc-700 rounded-lg"
                    >
                      <Edit2 className="w-4 h-4 text-zinc-400" />
                    </button>
                    <button onClick={() => deleteDiscount(discount.id)} className="p-2 hover:bg-red-500/20 rounded-lg">
                      <Trash2 className="w-4 h-4 text-zinc-400 hover:text-red-400" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {discounts.length === 0 && (
          <div className="text-center py-12 text-zinc-500">
            <Tag className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No discounts yet</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderReviews = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <select className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white">
          <option value="">All Reviews</option>
          <option value="pending">Pending Approval</option>
          <option value="approved">Approved</option>
        </select>
      </div>

      <div className="bg-zinc-900 rounded-xl border border-white/10 divide-y divide-zinc-800">
        {reviews.map(review => (
          <div key={review.id} className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-medium">{review.customer_name || 'Anonymous'}</span>
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-current' : ''}`} />
                    ))}
                  </div>
                  {review.is_verified_purchase && (
                    <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">Verified Purchase</span>
                  )}
                </div>
                <h4 className="font-medium mb-1">{review.title}</h4>
                <p className="text-zinc-400">{review.content}</p>
                <p className="text-sm text-zinc-500 mt-2">{new Date(review.created_at).toLocaleDateString()}</p>
              </div>
              <div className="flex items-center gap-2">
                {!review.is_approved && (
                  <Button onClick={() => moderateReview(review.id, true)} size="sm" className="bg-green-500">
                    <CheckCircle className="w-4 h-4 mr-1" />Approve
                  </Button>
                )}
                <Button onClick={() => moderateReview(review.id, false)} size="sm" variant="outline" className="border-zinc-700">
                  <XCircle className="w-4 h-4 mr-1" />Reject
                </Button>
              </div>
            </div>
          </div>
        ))}
        {reviews.length === 0 && (
          <div className="text-center py-12 text-zinc-500">
            <Star className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No reviews yet</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6 max-w-2xl">
      <div className="bg-zinc-900 rounded-xl border border-white/10 p-6">
        <h3 className="text-lg font-semibold mb-6">Store Settings</h3>
        
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Currency</label>
              <select
                value={settings.currency || 'USD'}
                onChange={(e) => setSettings({...settings, currency: e.target.value})}
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              >
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
                <option value="INR">INR (₹)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Decimal Places</label>
              <select
                value={settings.decimal_places || 2}
                onChange={(e) => setSettings({...settings, decimal_places: parseInt(e.target.value)})}
                className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              >
                <option value={0}>0</option>
                <option value={2}>2</option>
              </select>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="tax_included"
              checked={settings.tax_included || false}
              onChange={(e) => setSettings({...settings, tax_included: e.target.checked})}
              className="w-5 h-5 rounded"
            />
            <label htmlFor="tax_included">Prices include tax</label>
          </div>
          
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="enable_reviews"
              checked={settings.enable_reviews !== false}
              onChange={(e) => setSettings({...settings, enable_reviews: e.target.checked})}
              className="w-5 h-5 rounded"
            />
            <label htmlFor="enable_reviews">Enable product reviews</label>
          </div>
          
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="review_moderation"
              checked={settings.review_moderation !== false}
              onChange={(e) => setSettings({...settings, review_moderation: e.target.checked})}
              className="w-5 h-5 rounded"
            />
            <label htmlFor="review_moderation">Require review approval</label>
          </div>
          
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="guest_checkout"
              checked={settings.enable_guest_checkout !== false}
              onChange={(e) => setSettings({...settings, enable_guest_checkout: e.target.checked})}
              className="w-5 h-5 rounded"
            />
            <label htmlFor="guest_checkout">Allow guest checkout</label>
          </div>
          
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="stock_management"
              checked={settings.stock_management !== false}
              onChange={(e) => setSettings({...settings, stock_management: e.target.checked})}
              className="w-5 h-5 rounded"
            />
            <label htmlFor="stock_management">Enable stock management</label>
          </div>
          
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="enable_coupons"
              checked={settings.enable_coupons !== false}
              onChange={(e) => setSettings({...settings, enable_coupons: e.target.checked})}
              className="w-5 h-5 rounded"
            />
            <label htmlFor="enable_coupons">Enable discount codes</label>
          </div>
          
          <div className="pt-4">
            <Button onClick={saveSettings} className="bg-violet-500">
              <Save className="w-4 h-4 mr-2" />Save Settings
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return renderDashboard();
      case 'products': return renderProducts();
      case 'orders': return renderOrders();
      case 'subscriptions': return renderSubscriptions();
      case 'discounts': return renderDiscounts();
      case 'reviews': return renderReviews();
      case 'commerce': return (
        <CommerceExtensionsPanel
          websiteId={websiteId}
          token={token}
          API_URL={API_URL}
          orders={orders}
          onRefresh={fetchAllData}
        />
      );
      case 'settings': return renderSettings();
      default: return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button onClick={() => navigate(`/edit/${websiteId}`)} variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Editor
          </Button>
          <div className="h-6 w-px bg-zinc-700" />
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-violet-400" />
            <h1 className="text-lg font-semibold text-white">E-commerce Panel</h1>
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-zinc-900 border-r border-white/10 min-h-[calc(100vh-73px)]">
          <div className="p-4 space-y-1">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    activeTab === tab.id 
                      ? 'bg-violet-500/20 text-violet-400' 
                      : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-sm">{tab.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-8">
          {renderContent()}
        </div>
      </div>

      {/* Product Modal */}
      {showProductModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-zinc-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 border border-white/10">
            <h3 className="text-xl font-bold mb-6">{editingItem ? 'Edit Product' : 'Add Product'}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Product Name *</label>
                <input
                  type="text"
                  value={productForm.name}
                  onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                  placeholder="Enter product name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={productForm.description}
                  onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white resize-none"
                  rows={3}
                  placeholder="Product description"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Price *</label>
                  <input
                    type="number"
                    value={productForm.price}
                    onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                    placeholder="0.00"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Compare at Price</label>
                  <input
                    type="number"
                    value={productForm.compare_at_price}
                    onChange={(e) => setProductForm({...productForm, compare_at_price: e.target.value})}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                    placeholder="Original price (optional)"
                    step="0.01"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">SKU</label>
                  <input
                    type="text"
                    value={productForm.sku}
                    onChange={(e) => setProductForm({...productForm, sku: e.target.value})}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                    placeholder="SKU-001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Stock Quantity</label>
                  <input
                    type="number"
                    value={productForm.stock}
                    onChange={(e) => setProductForm({...productForm, stock: e.target.value})}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                    placeholder="0"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <select
                  value={productForm.category_id}
                  onChange={(e) => setProductForm({...productForm, category_id: e.target.value})}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                >
                  <option value="">No Category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={productForm.is_active}
                    onChange={(e) => setProductForm({...productForm, is_active: e.target.checked})}
                    className="w-5 h-5 rounded"
                  />
                  <span>Active</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={productForm.is_featured}
                    onChange={(e) => setProductForm({...productForm, is_featured: e.target.checked})}
                    className="w-5 h-5 rounded"
                  />
                  <span>Featured</span>
                </label>
              </div>
              <div className="pt-4 flex gap-3">
                <Button onClick={() => { setShowProductModal(false); setEditingItem(null); }} variant="outline" className="flex-1 border-zinc-700">Cancel</Button>
                <Button onClick={saveProduct} className="flex-1 bg-violet-500">{editingItem ? 'Update' : 'Create'} Product</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Discount Modal */}
      {showDiscountModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-zinc-900 rounded-2xl w-full max-w-lg p-6 border border-white/10">
            <h3 className="text-xl font-bold mb-6">{editingItem ? 'Edit Discount' : 'Create Discount'}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Discount Code *</label>
                <input
                  type="text"
                  value={discountForm.code}
                  onChange={(e) => setDiscountForm({...discountForm, code: e.target.value.toUpperCase()})}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white font-mono"
                  placeholder="SAVE20"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Name</label>
                <input
                  type="text"
                  value={discountForm.name}
                  onChange={(e) => setDiscountForm({...discountForm, name: e.target.value})}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                  placeholder="20% Off Sale"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Type</label>
                  <select
                    value={discountForm.type}
                    onChange={(e) => setDiscountForm({...discountForm, type: e.target.value})}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed_amount">Fixed Amount</option>
                    <option value="free_shipping">Free Shipping</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Value</label>
                  <input
                    type="number"
                    value={discountForm.value}
                    onChange={(e) => setDiscountForm({...discountForm, value: e.target.value})}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                    placeholder={discountForm.type === 'percentage' ? '20' : '10.00'}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Minimum Purchase</label>
                  <input
                    type="number"
                    value={discountForm.min_purchase}
                    onChange={(e) => setDiscountForm({...discountForm, min_purchase: e.target.value})}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Max Uses (0 = unlimited)</label>
                  <input
                    type="number"
                    value={discountForm.max_uses}
                    onChange={(e) => setDiscountForm({...discountForm, max_uses: e.target.value})}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                    placeholder="0"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={discountForm.is_active}
                  onChange={(e) => setDiscountForm({...discountForm, is_active: e.target.checked})}
                  className="w-5 h-5 rounded"
                />
                <span>Active</span>
              </label>
              <div className="pt-4 flex gap-3">
                <Button onClick={() => { setShowDiscountModal(false); setEditingItem(null); }} variant="outline" className="flex-1 border-zinc-700">Cancel</Button>
                <Button onClick={saveDiscount} className="flex-1 bg-violet-500">{editingItem ? 'Update' : 'Create'} Discount</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Plan Modal */}
      {showPlanModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-zinc-900 rounded-2xl w-full max-w-lg p-6 border border-white/10">
            <h3 className="text-xl font-bold mb-6">{editingItem ? 'Edit Plan' : 'Create Plan'}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Plan Name *</label>
                <input
                  type="text"
                  value={planForm.name}
                  onChange={(e) => setPlanForm({...planForm, name: e.target.value})}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                  placeholder="Pro Plan"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <input
                  type="text"
                  value={planForm.description}
                  onChange={(e) => setPlanForm({...planForm, description: e.target.value})}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                  placeholder="Best for growing businesses"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Price *</label>
                  <input
                    type="number"
                    value={planForm.price}
                    onChange={(e) => setPlanForm({...planForm, price: e.target.value})}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                    placeholder="29.00"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Billing Interval</label>
                  <select
                    value={planForm.interval}
                    onChange={(e) => setPlanForm({...planForm, interval: e.target.value})}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                  >
                    <option value="month">Monthly</option>
                    <option value="year">Yearly</option>
                    <option value="week">Weekly</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Features (one per line)</label>
                <textarea
                  value={planForm.features}
                  onChange={(e) => setPlanForm({...planForm, features: e.target.value})}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white resize-none"
                  rows={4}
                  placeholder="Unlimited access&#10;Priority support&#10;Custom branding"
                />
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={planForm.is_active}
                    onChange={(e) => setPlanForm({...planForm, is_active: e.target.checked})}
                    className="w-5 h-5 rounded"
                  />
                  <span>Active</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={planForm.is_popular}
                    onChange={(e) => setPlanForm({...planForm, is_popular: e.target.checked})}
                    className="w-5 h-5 rounded"
                  />
                  <span>Mark as Popular</span>
                </label>
              </div>
              <div className="pt-4 flex gap-3">
                <Button onClick={() => { setShowPlanModal(false); setEditingItem(null); }} variant="outline" className="flex-1 border-zinc-700">Cancel</Button>
                <Button onClick={savePlan} className="flex-1 bg-violet-500">{editingItem ? 'Update' : 'Create'} Plan</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EcommercePanel;
